/* Service days as a list and as a trend — shared by the Attendance
 * dashboard, the full list of days, and each service's own page.
 *
 * A day links to its report. The link carries where it came from, so the
 * report's back link returns there rather than always to one place.
 */
import { Link } from 'react-router-dom'
import { ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { Occurrence } from '@/types/api'
import { day } from './parts'

export type Back = { back: string; backLabel: string }

/** Present as a share of expected, or null when nobody was expected. */
export function rate(o: Occurrence): number | null {
  return o.counts.expected > 0 ? o.counts.present / o.counts.expected : null
}

export function percent(r: number | null): string {
  return r === null ? '—' : `${Math.round(r * 100)}%`
}

/** The average rate over the days that are over. */
export function averageRate(days: Occurrence[]): number | null {
  const rates = days.filter((o) => o.state === 'over').map(rate).filter((r): r is number => r !== null)
  if (!rates.length) return null
  return rates.reduce((a, b) => a + b, 0) / rates.length
}

export function OnNowChip({ state }: { state: Occurrence['state'] }) {
  if (state === 'on') {
    return (
      <span className="rounded-pill bg-success-soft px-s2 py-0.5 text-xs font-semibold text-success-ink">
        On now
      </span>
    )
  }
  if (state === 'upcoming') {
    return (
      <span className="rounded-pill bg-surface-2 px-s2 py-0.5 text-xs font-semibold text-ink-2">
        Later today
      </span>
    )
  }
  return null
}

export function DayRow({
  occurrence: o,
  from,
  showName = true,
}: {
  occurrence: Occurrence
  from: Back
  showName?: boolean
}) {
  const c = o.counts
  const r = rate(o)
  return (
    <Link
      to={`/attendance/days/${o.id}`}
      state={from}
      className="flex items-center gap-s3 px-s4 py-s3 transition-colors hover:bg-hover"
    >
      <div className="min-w-0 flex-1">
        <p className="flex flex-wrap items-center gap-s2 text-base font-semibold">
          <span className="truncate">{showName ? o.name : day(o.date, true)}</span>
          <OnNowChip state={o.state} />
        </p>
        <p className="text-sm text-ink-2">
          {showName ? `${day(o.date)} · ` : ''}
          {c.present} of {c.expected} present
          {c.excused ? ` · ${c.excused} excused` : ''}
          {o.state === 'over'
            ? c.absent
              ? ` · ${c.absent} absent`
              : ''
            : c.not_yet
              ? ` · ${c.not_yet} not yet`
              : ''}
          {c.visitors ? ` · ${c.visitors} visitors` : ''}
        </p>
      </div>
      {o.state === 'over' && r !== null ? (
        <span className="shrink-0 text-base font-bold tabular-nums text-ink-2">{percent(r)}</span>
      ) : null}
      <ChevronRight className="h-5 w-5 shrink-0 text-ink-3" aria-hidden />
    </Link>
  )
}

/** One bar per day, oldest on the left: how many came against how many were
 *  expected. Each bar opens that day. */
export function TrendBars({
  days,
  from,
  height = 72,
}: {
  days: Occurrence[]
  from: Back
  height?: number
}) {
  const ordered = [...days].reverse()
  const top = Math.max(1, ...ordered.map((o) => Math.max(o.counts.expected, o.counts.present)))
  return (
    <div className="flex items-end gap-1.5" style={{ height }} role="list" aria-label="Attendance by day">
      {ordered.map((o) => {
        const c = o.counts
        const expectedH = (Math.max(c.expected, c.present) / top) * height
        const presentH = (c.present / top) * height
        return (
          <Link
            key={o.id}
            role="listitem"
            to={`/attendance/days/${o.id}`}
            state={from}
            title={`${day(o.date)} · ${c.present} of ${c.expected} present`}
            aria-label={`${day(o.date)}: ${c.present} of ${c.expected} present`}
            className="group relative flex h-full min-w-[10px] max-w-[36px] flex-1 items-end"
          >
            <span
              className="absolute inset-x-0 bottom-0 rounded-t bg-surface-2 group-hover:bg-hover-2"
              style={{ height: Math.max(2, expectedH) }}
              aria-hidden
            />
            <span
              className={cn(
                'relative w-full rounded-t',
                'bg-brand', o.state !== 'over' && 'opacity-50',
              )}
              style={{ height: c.present ? Math.max(2, presentH) : 0 }}
              aria-hidden
            />
          </Link>
        )
      })}
    </div>
  )
}
